import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { CURRENCIES } from '@/types/expense';

const CURRENCY_LOCALES: Record<string, string> = {
  DKK: 'da-DK',
  USD: 'en-US',
  EUR: 'de-DE',
};

interface CurrencyPreferenceContextType {
  defaultCurrency: string;
  setDefaultCurrency: (currency: string) => void;
  getCurrencySymbol: (code?: string) => string;
  formatAmount: (amount: number, currencyCode?: string, options?: { showSymbol?: boolean; maximumFractionDigits?: number; minimumFractionDigits?: number }) => string;
}

const CurrencyPreferenceContext = createContext<CurrencyPreferenceContextType | undefined>(undefined);

export function CurrencyPreferenceProvider({ children }: { children: ReactNode }) {
  const [defaultCurrency, setDefaultCurrency] = useState('DKK');

  const getCurrencySymbol = (code?: string) => {
    const currency = CURRENCIES.find(c => c.code === (code || defaultCurrency));
    return currency?.symbol || 'kr';
  };

  const formatAmount = useCallback((
    amount: number,
    currencyCode?: string,
    options?: { showSymbol?: boolean; maximumFractionDigits?: number; minimumFractionDigits?: number }
  ) => {
    const code = currencyCode || defaultCurrency;
    const locale = CURRENCY_LOCALES[code] || 'en-US';
    const showSymbol = options?.showSymbol ?? true;
    const minFrac = options?.minimumFractionDigits ?? 2;
    const maxFrac = options?.maximumFractionDigits ?? 2;

    if (showSymbol) {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: minFrac,
        maximumFractionDigits: maxFrac,
      }).format(amount);
    }
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: minFrac,
      maximumFractionDigits: maxFrac,
    }).format(amount);
  }, [defaultCurrency]);

  return (
    <CurrencyPreferenceContext.Provider value={{ defaultCurrency, setDefaultCurrency, getCurrencySymbol, formatAmount }}>
      {children}
    </CurrencyPreferenceContext.Provider>
  );
}

export function useCurrencyPreference() {
  const context = useContext(CurrencyPreferenceContext);
  if (context === undefined) {
    throw new Error('useCurrencyPreference must be used within a CurrencyPreferenceProvider');
  }
  return context;
}
